import { z } from "zod";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/shared/lib/utils";
import {
  ChartSkeleton,
  CHART_TOOLTIP,
  CHART_GRID,
  CHART_AXIS_TICK,
  CHART_COLORS,
} from "@/shared/components";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const TrendLineSchema = z.object({
  series: z.array(
    z.object({
      name: z.string(),
      data: z.array(
        z.object({
          date: z.string(),
          value: z.number(),
        }),
      ),
    }),
  ),
  xLabel: z.string().optional(),
  yLabel: z.string().optional(),
});

type TrendLineData = z.infer<typeof TrendLineSchema>;

/* ────────────────────── Helpers ────────────────────── */

type MergedDataPoint = { date: string } & Record<string, number | string>;

/** 다중 시리즈 → Recharts가 기대하는 {date, [seriesName]: value}[] 형태로 변환 */
function mergeSeries(series: TrendLineData["series"]): MergedDataPoint[] {
  const dateMap = new Map<string, MergedDataPoint>();

  for (const s of series) {
    for (const point of s.data) {
      if (!dateMap.has(point.date)) {
        dateMap.set(point.date, { date: point.date });
      }
      dateMap.get(point.date)![s.name] = point.value;
    }
  }

  return Array.from(dateMap.values()).sort((a, b) =>
    String(a.date).localeCompare(String(b.date)),
  );
}

/* ────────────────────── Component ────────────────────── */

export function TrendLine({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<TrendLineData>) {
  const { series, yLabel } = widget.data;
  const capped = series.slice(0, 4);
  const chartData = mergeSeries(capped);

  return (
    <div
      className={cn("w-full", className)}
      style={{ height: 240 }}
      onClick={
        widget.deepLink && onDeepLink
          ? () => onDeepLink(widget.deepLink!)
          : undefined
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={chartData}
          margin={{ top: 4, right: 12, left: 0, bottom: 0 }}
        >
          <CartesianGrid {...CHART_GRID} />
          <XAxis
            dataKey="date"
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={CHART_AXIS_TICK}
            tickLine={false}
            axisLine={false}
            label={
              yLabel
                ? {
                    value: yLabel,
                    angle: -90,
                    position: "insideLeft",
                    style: { fontSize: 10 },
                  }
                : undefined
            }
          />
          <Tooltip {...CHART_TOOLTIP} />
          {capped.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {capped.map((s, idx) => (
            <Line
              key={s.name}
              type="monotone"
              dataKey={s.name}
              stroke={CHART_COLORS[idx % CHART_COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function TrendLineSkeleton({ className }: { className?: string }) {
  return <ChartSkeleton height={240} className={className} />;
}

/* ────────────────────── Widget Definition ────────────────────── */

const trendLineWidget = defineWidget<TrendLineData>({
  type: WIDGET_TYPES.TREND_LINE,
  label: "트렌드 라인 차트",
  schema: TrendLineSchema,
  component: TrendLine,
  skeleton: TrendLineSkeleton,
  defaultLayout: "full",
  maxHeight: 280,
  aiHint: "키워드/채널 시계열 트렌드 (월별 검색량, 클릭 수 등)",
});

registerWidget(trendLineWidget);
